import React, { useEffect } from 'react';

import { Outlet, useLocation, useParams } from 'react-router-dom';

import useMediaQuery from '@/hooks/useMediaQuery.hook';
import { useSettings } from '@/hooks/useSettings.hook';

import { DesktopSideNavbar } from '@/components/DesktopSidebarNav';
import Footer from '@/components/Footer';
import Header from '@/components/Header';
import HeaderDesktop from '@/components/HeaderDesktop';
import MobileNavigation from '@/components/Layout/Navigation/MobileNavigation';
import PageNavigation from '@/components/PageNavigation';

import { MediaQueriesEnum } from '@/enums/mediaQueries.enum';
import { RoutesEnum } from '@/enums/routes.enum';

import { addIdToRoute } from '@/routes/types';

import { IS_HEADER_HIDDEN } from '@/utils/constants';

import { Main, MainInner } from './Layout.styles';
import Sidebar from './Sidebar';
import SidebarBottom from './SidebarBottom/SidebarBottom';
import SidebarContent from './SidebarContent';
import SidebarTop from './SidebarTop';

const Layout: React.FC = () => {
  const { id } = useParams();
  const { isMobile, isDesktop } = useSettings();
  const [isSidebarVisible, setIsSidebarVisible] = React.useState(false);
  const { pathname } = useLocation();
  const rootElement = document.getElementById('root');

  const handleOpenSidebar = React.useCallback(
    () => setIsSidebarVisible(true),
    [setIsSidebarVisible],
  );

  const handleCloseSidebar = React.useCallback(
    () => setIsSidebarVisible(false),
    [setIsSidebarVisible],
  );

  useEffect(() => {
    if (isSidebarVisible && rootElement) {
      document.body.style.overflow = 'hidden';
      rootElement.style.overflow = 'hidden';
    } else if (!isSidebarVisible && rootElement) {
      document.body.style.overflow = 'auto';
      rootElement.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
      (rootElement as any).style.overflow = 'auto';
    };
  }, [isSidebarVisible, rootElement]);

  useEffect(() => {
    handleCloseSidebar();
  }, [handleCloseSidebar, pathname]);

  useEffect(
    () => window.parent.postMessage({ type: 'scrollTop' }, '*'),
    [pathname],
  );

  const isOnboardingPage = pathname === RoutesEnum.onboarding;
  const isRulesPage = pathname === RoutesEnum.rules;
  const isContestRulesPage =
    pathname === addIdToRoute(RoutesEnum.contestRules, id);

  const renderHeader = () => {
    if (isDesktop) {
      return <HeaderDesktop />;
    }
    if (!isOnboardingPage) {
      return <Header handleClick={handleOpenSidebar} />;
    }
    return null;
  };

  return (
    <>
      {!IS_HEADER_HIDDEN && renderHeader()}

      {isSidebarVisible && !IS_HEADER_HIDDEN && (
        <Sidebar
          BottomComponent={<SidebarBottom />}
          ContentComponent={<SidebarContent />}
          TopComponent={<SidebarTop />}
          isVisible={isSidebarVisible}
          onCloseClick={handleCloseSidebar}
        />
      )}

      {isOnboardingPage || isRulesPage || isContestRulesPage ? (
        <>
          <Outlet />
          {!IS_HEADER_HIDDEN && <Footer />}

          <DesktopSideNavbar />
        </>
      ) : (
        <>
          {!isDesktop && IS_HEADER_HIDDEN && <MobileNavigation />}

          <Main>
            {pathname !== RoutesEnum.game && <PageNavigation />}
            <MainInner>
              <Outlet />
            </MainInner>
          </Main>

          {!IS_HEADER_HIDDEN && <Footer />}

          <DesktopSideNavbar />
        </>
      )}
    </>
  );
};

export default Layout;
